import "./App.css";
import React, {
  useContext,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Login from "./components/login/Login";
import Header from "./core/routes/header/Header";
import Footer from "./core/routes/footer/Footer";
import RequestErrorMessage from "./components/_components/request-error-message/RequestErrorMessage";
import {
  deleteCookie,
  getCookie,
  setCookie,
  showToast,
} from "./core/common-service/CommonService";
import {
  postRefreshToken,
  setDefaultHeader,
  getUserSubscription,
} from "./core/api/Api";
import { StoreContext } from "./core/context/StoreContext";
import Register from "./components/register/Register";
import ConfirmRegister from "./components/confirm-register/ConfirmRegister";
import LandingPage from "./components/home/LandingPage";
import FinishedBookOptions from "./components/home/FinishedBookOptions";
import Questions from "./components/home/general-categories/general-categories-options/questions/Questions";
import ProtectedRoute from "./core/routes/protected_route/ProtectedRoute";
import GeneralCategories from "./components/home/general-categories/GeneralCategories";
import WritingModeChoice from "./components/home/general-categories/WritingModeChoice";
import QuestionCategories from "./components/home/general-categories/general-categories-options/GeneralCategoriesOptions";
import ForgotPassword from "./components/forgot-password/ForgotPassword";
import ResetPassword from "./components/reset-password/ResetPassword";
import MyBooks from "./components/my-books/MyBooks";
import Subscriptions from "./components/subscriptions/Subscriptions";
import { useTranslation } from "react-i18next";
import Payment from "./components/payment/Payment";
import Profile from "./components/profile/Profile";
import GiftCard from "./components/gift-card/GiftCard";
import GiftCardPayment from "./components/gift-card/gift-card-payment/GiftCardPayment";
import ManageSubscriptions from "./components/subscriptions/ManageSubscriptions";
import PaymentMethods from "./components/payment-methods/PaymentMethods";
import UpdatePaymentMethod from "./components/update-payment-method/UpdatePaymentMethod";
import GiftCardForm from "./components/gift-card/gift-card-form/GiftCardForm";
import ThankYouPurchase from "./components/thankyou/ThankYouPurchase";
import { ThankYouRegister } from "./components/thankyou/ThankYouRegister";
import PrintingSection from "./components/printing/PrintingSection";
import PrintingPaymentPage from "./components/printing/PrintingPaymentPage";
import ScrollToTop from "./components/common/ScrollToTop";
import useLangNavigate from "./components/common/useLangNavigate";
import { Navigate } from "react-router-dom";
import { trackPageView } from "./core/utils/trackPageView";
import { getPageTrackingData } from "./core/utils/pageMapping";
import { useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import AudiobookPayment from "./components/home/general-categories/general-categories-options/questions/components/audio-book-payment/AudiobookPayment";
import StandaloneAudiobookUpload from "./components/home/general-categories/general-categories-options/questions/components/audio-book-upload/StandaloneAudiobookUpload";
import LanguageRedirect from "./components/common/LanguageRedirect";
import LegacyLocaleRedirect from "./components/common/LegacyLocaleRedirect";
import Feedback from "./components/feedback/Feedback";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  LEGACY_LOCALE_MAP,
  getLocaleFromPath,
  withLocale,
} from "./config/locales";
import CalendlyBooking from "./components/calendly/CalendlyBooking";
import ProofreadingEstimation from "./components/proofreading/ProofreadingEstimation";

// Built once at module load — strips the locale prefix from any pathname.
// Covers both BCP 47 codes (en-GB) and legacy codes (uk).
const _localeCodes = [
  ...SUPPORTED_LOCALES.map((l) => l.code),
  ...Object.keys(LEGACY_LOCALE_MAP),
]
  .map((c) => c.replace("-", "\\-"))
  .join("|");
const LOCALE_PREFIX_RE = new RegExp(`^\\/(${_localeCodes})(?=\\/|$)`);
const PaymentFailedModal = () => {
  const { paymentFailed, setPaymentFailed } = useContext(StoreContext);
  const { t } = useTranslation();
  const navigate = useLangNavigate();

  if (!paymentFailed) return null;

  const dismiss = () => {
    localStorage.setItem("payment_failed_modal_shown", "1");
    setPaymentFailed(false);
  };

  const handleCta = () => {
    localStorage.setItem("payment_failed_modal_shown", "1");
    setPaymentFailed(false);
    navigate("/update-payment-method");
  };

  return (
    <div className="payment-failed-overlay">
      <div className="payment-failed-modal">
        <button className="payment-failed-modal-close" onClick={dismiss}>
          &times;
        </button>
        <h2>{t("subscribe.payment_failed_title")}</h2>
        <p>{t("subscribe.payment_failed_message")}</p>
        <button className="payment-failed-modal-btn" onClick={handleCta}>
          {t("subscribe.update_payment_method")}
        </button>
      </div>
    </div>
  );
};

const PreLoader = () => {
  const { setIsLoggedIn, user, setUser, setPaymentFailed } =
    useContext(StoreContext);
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const location = useLocation();
  const [authReady, setAuthReady] = useState(false);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const normalizePath = (pathname) => pathname.replace(LOCALE_PREFIX_RE, "");

  useEffect(() => {
    if (!authReady) return;
    const normalizedPath = normalizePath(location.pathname);
    const trackingData = getPageTrackingData(normalizedPath);
    if (trackingData) {
      trackPageView({ ...trackingData, user: userRef.current });
    }
  }, [location, authReady]);

  const checkToken = useCallback(() => {
    const token = sessionStorage.getItem("token") || getCookie("token");

    if (token) {
      setDefaultHeader("Bearer " + token);
      postRefreshToken()
        .then((res) => {
          const response = res.data;
          if (response?.success) {
            const token = response?.success?.token;
            const user = response?.success?.user;
            if (user?.is_verified) {
              setIsLoggedIn(true);
              setUser(user);
              Sentry.setUser({ id: user.id, email: user.email });
              setCookie("token", token);
              getUserSubscription()
                .then((subRes) => {
                  const sub = subRes?.data;
                  if (
                    sub?.id &&
                    sub?.cancel_date &&
                    sub?.cancellation_reason === "payment_failed"
                  ) {
                    if (!localStorage.getItem("payment_failed_modal_shown")) {
                      setPaymentFailed(true);
                    }
                  } else {
                    // Payment resolved — reset flag so modal shows if they fail again in future
                    localStorage.removeItem("payment_failed_modal_shown");
                  }
                })
                .catch(() => {})
                .finally(() => setAuthReady(true));
            } else {
              showToast({
                type: "error",
                message: t("error.user_not_allowed"),
              });
              setAuthReady(true);
            }
          } else {
            setIsLoggedIn(false);
            sessionStorage.clear();
            deleteCookie("token");
            setAuthReady(true);
            navigate("/login", getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE);
          }
        })
        .catch((e) => {
          setIsLoggedIn(false);
          sessionStorage.clear();
          deleteCookie("token");
          setAuthReady(true);
          navigate("/login", getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE);
        });
    } else {
      setIsLoggedIn(false);
      setAuthReady(true);
    }
  }, [navigate, t, setIsLoggedIn, setUser, setAuthReady]);

  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    checkToken();
  }, [checkToken]);

  return <Outlet />;
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Legacy locale redirects live here so static "/uk" beats dynamic "/:lang" in RRv6 ranking */}
        {Object.keys(LEGACY_LOCALE_MAP).map((code) => (
          <Route key={code} path={`/${code}/*`} element={<LegacyLocaleRedirect />} />
        ))}
        <Route path="/:lang/*" element={<PreLoader />} />
      </Routes>
      <Header />
      <PaymentFailedModal />
      <RequestErrorMessage />
      <main className="wrapper">
        <Routes>
          {/* Default route - now goes to landing page */}
          <Route path="/" element={<LanguageRedirect />} />

          {/* Landing page route */}
          <Route path="/:lang/home" element={<LandingPage />} />

          {/* Finished book options route */}
          <Route
            path="/:lang/finished-book-options"
            element={<FinishedBookOptions />}
          />

          {/* Auth routes */}
          <Route path="/:lang/login" element={<Login />} />
          <Route path="/:lang/register" element={<Register />} />
          <Route path="/:lang/forgot-password" element={<ForgotPassword />} />
          <Route path="/:lang/reset-password" element={<ResetPassword />} />

          {/* Feedback route */}
          <Route path="/:lang/feedback" element={<Feedback />} />

          {/* Calendly booking route */}
          <Route path="/:lang/book-appointment" element={<CalendlyBooking />} />

          {/* Protected routes */}
          <Route
            path="/:lang/my-books"
            element={
              <ProtectedRoute>
                <MyBooks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:lang/manage-subscriptions"
            element={
              <ProtectedRoute>
                <ManageSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:lang/payment-methods"
            element={
              <ProtectedRoute>
                <PaymentMethods />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:lang/update-payment-method"
            element={
              <ProtectedRoute>
                <UpdatePaymentMethod />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:lang/subscriptions"
            element={
              <ProtectedRoute>
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:lang/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Writing mode choice (AI-assisted vs self-write) */}
          <Route
            path="/:lang/writing-mode/:service_id/:flow_type"
            element={<WritingModeChoice />}
          />

          {/* General categories with flow type */}
          <Route
            path="/:lang/general-categories/:service_id/:flow_type"
            element={<GeneralCategories />}
          />
          <Route
            path="/:lang/general-categories-options/:service_id/:general_category_id/:flow_type"
            element={<QuestionCategories />}
          />

          {/* Questions route */}
          <Route
            path="/:lang/questions/:service_session_id"
            element={<Questions />}
          />

          {/* Payment routes */}
          <Route path="/:lang/payment" element={<Payment />} />
          <Route path="/:lang/gift-card" element={<GiftCard />} />
          <Route
            path="/:lang/gift-card-payment"
            element={<GiftCardPayment />}
          />
          <Route path="/:lang/gift-card-form" element={<GiftCardForm />} />
          <Route
            path="/:lang/audiobook-payment"
            element={<AudiobookPayment />}
          />

          <Route
            path="/:lang/audiobook-upload"
            element={
              <ProtectedRoute>
                <StandaloneAudiobookUpload />
              </ProtectedRoute>
            }
          />

          {/* Thank you pages */}
          <Route
            path="/:lang/thankyou/purchase"
            element={<ThankYouPurchase />}
          />
          <Route
            path="/:lang/thankyou/register"
            element={<ThankYouRegister />}
          />

          {/* Proofreading estimation route */}
          <Route
            path="/:lang/proofreading-estimation"
            element={<ProofreadingEstimation />}
          />

          {/* Printing routes */}
          <Route
            path="/:lang/printing"
            element={<PrintingSection />}
          />
          <Route
            path="/:lang/printing-payment"
            element={<PrintingPaymentPage />}
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<LanguageRedirect />} />
        </Routes>
      </main>
      <Footer />
    </BrowserRouter>
  );
}
